import {
	ChangeEvent, FC, useMemo, useState,
} from 'react';
import {
	alephToast,
	Avatar,
	AvatarSize,
	BackArrowIcon,
	BanIcon,
	Button,
	Dialog,
	Input, isReddit,
	LoaderBars,
	NotificationToast,
	NotificationType,
	ExchangeIcon,
	CrossRightIcon,
	getAvatarSource,
} from '@aleph/shared-components';
import { AdvertiserOwnerOptionsQueryUsers } from 'wb-reddit/graphql/generated';
import { captureException } from '@sentry/react';
import { useTranslation } from 'react-i18next';
import { mapTypeNameToAvatarEntity } from 'wb-reddit/app/common/utils';
import { getUserInitials } from 'wb-reddit/app/routes/users/utils';

type OwnerChangeDialogProps = {
	onSubmit: () => Promise<void>;
	onClose: () => void;
	type?: 'team' | 'advertiser';
	oldOwner?: AdvertiserOwnerOptionsQueryUsers | null;
	newOwner?: AdvertiserOwnerOptionsQueryUsers | null;
};

const OwnerChangeDialog: FC<OwnerChangeDialogProps> = ({
	onSubmit,
	type = 'advertiser',
	onClose,
	oldOwner,
	newOwner,
}) => {
	const { t } = useTranslation();
	const [newOwnerFirstName, setNewOwnerFirstName] = useState('');
	const [saving, setSaving] = useState(false);
	const disabled = useMemo(() => {
		if (saving) return true;

		if (isReddit()) {
			return newOwnerFirstName !== newOwner?.userFullName?.split?.(' ')?.[0];
		}

		// @ts-ignore
		return newOwnerFirstName !== newOwner?.userName;
	}, [newOwner, newOwnerFirstName]);

	return (
		<Dialog
			isOpen
			className="!max-h-[75vh] z-[1001] !w-[600px]"
			onClose={onClose}
		>
			<Dialog.Header
				preset={{
					prefix: <ExchangeIcon />,
					title:  type === 'team'
						? t('re: advertiser.ownership.transfer.team', 'Transfer team ownership')
						: t('re: advertiser.ownership.transfer.advertiser', 'Transfer advertiser ownership'),
				}}
			/>
			<Dialog.Content>
				<div className="flex flex-col gap-[16px] pb-[16px]">
					<div className="text-[16px] text-gray-800 font-semibold">
						{t('re: advertiser.ownership.transfer.change_is_correct', 'Are you sure this change is correct?')}
					</div>
					<div className="text-[13px] text-gray-800">
						{t('re: advertiser.ownership.transfer.familiarise', { type })}
					</div>

					<div className="flex items-center justify-center gap-[32px] overflow-hidden">
						<div className="flex flex-col gap-[16px] items-center justify-center overflow-hidden flex-1">
							<Avatar
								size={AvatarSize.L}
								src={getAvatarSource(oldOwner)}
								entityType={mapTypeNameToAvatarEntity(oldOwner)}
								entityUserInitials={getUserInitials(oldOwner)}
							/>

							<div className="text-center w-full overflow-hidden overflow-ellipsis">
								<div className="text-gray-600 text-[11px]">PREVIOUS</div>
								<div className="text-gray-800 text-[13px] overflow-hidden overflow-ellipsis font-semibold">
									{oldOwner?.userFullName}
								</div>
								<div className="text-gray-800 text-[12px] overflow-hidden overflow-ellipsis">
									{oldOwner?.userEmail}
								</div>
							</div>
						</div>

						<CrossRightIcon
							width={64}
							height={64}
							fill={'hsl(var(--gray-600))'}
						/>

						<div className="flex flex-col gap-[16px] items-center justify-center overflow-hidden flex-1">
							<Avatar
								size={AvatarSize.L}
								src={getAvatarSource(oldOwner)}
								entityType={mapTypeNameToAvatarEntity(oldOwner)}
								entityUserInitials={getUserInitials(oldOwner)}
							/>

							<div className="text-center w-full overflow-hidden overflow-ellipsis">
								<div className="text-gray-600 text-[11px]">NEW</div>
								<div className="text-gray-800 text-[13px] font-semibold overflow-hidden overflow-ellipsis">
									{newOwner?.userFullName}
								</div>
								<div className="text-gray-800 text-[12px] overflow-hidden overflow-ellipsis">
									{newOwner?.userEmail}
								</div>
							</div>
						</div>
					</div>

					<div className="text-[16px] text-gray-800 font-semibold">
						{t('re: advertiser.ownership.transfer.unlock', 'Unlock confirmation')}
					</div>

					<Input
						className="w-full"
						label={t('re: advertiser.ownership.transfer.new_owner', 'New owner\'s first name')}
						instructionText={t('re: advertiser.ownership.transfer.instruction', 'Required to prevent accidental clicks.')}
						value={newOwnerFirstName}
						onChange={(e: ChangeEvent<HTMLInputElement>) => setNewOwnerFirstName(e.target.value)}
					/>
				</div>
			</Dialog.Content>

			<Dialog.Footer>
				<Button
					appearance="ghost"
					color="gray"
					leftNode={<BanIcon />}
					title={t('sc: close', 'Close')}
					onClick={onClose}
				/>

				<Button
					leftNode={saving ? <LoaderBars className="animate-spin" /> : <BackArrowIcon />}
					title={t('re: advertiser.ownership.transfer.button', 'Transfer ownership')}
					disabled={disabled}
					onClick={async () => {
						setSaving(true);

						try {
							await onSubmit();

							alephToast(
								<NotificationToast
									type={NotificationType.Success}
									title={t('re: advertiser.ownership.transfer.success', 'Ownership successfully transferred.')}
								/>,
							);
						} catch (e) {
							captureException(e);
							alephToast(
								<NotificationToast
									type={NotificationType.Error}
									title={t('sc: action err', { action: 'transferring the ownership. We have been notified' })}
								/>,
							);
						}

						setSaving(false);
						onClose();
					}}
				/>
			</Dialog.Footer>
		</Dialog>
	);
};

export default OwnerChangeDialog;
